import * as React from "react";

import { ScrollableChat } from "@/components/feature/chat";
import { usePinnedTurn } from "@/components/feature/chat/usePinnedTurn";
import { cn } from "@/lib/utils";
import type { Evidence } from "@/types/evidences";
import type { Artefact, StreamMessage } from "@/modules/chat/chat-ui.types";
import { groupIntoTurns } from "@/modules/chat/chat.utils";
import { AssistantMessage } from "./AssistantMessage";
import { UserMessage } from "./UserMessage";

interface Props {
  messages: StreamMessage[];
  lastSentMessageId: string | null;
  evidences: Evidence[];
  isStreaming: boolean;
  streamingMessageId: string | null;
  onArtefactClick?: (artefactId: string) => void;
  inlineArtefacts?: Artefact[];
  children?: React.ReactNode;
}

export const ChatTranscript: React.FC<Props> = ({
  messages,
  lastSentMessageId,
  evidences,
  isStreaming,
  streamingMessageId,
  onArtefactClick,
  inlineArtefacts,
  children,
}) => {
  const { chatRef, pinnedTurnRef } = usePinnedTurn(lastSentMessageId);
  const turns = React.useMemo(() => groupIntoTurns(messages, message => message.isUser), [messages]);

  return (
    <ScrollableChat ref={chatRef} className="absolute inset-0">
      {children}
      {turns.map(turn => {
        const isPinned = turn[0].id === lastSentMessageId;

        return (
          <div key={turn[0].id} ref={isPinned ? pinnedTurnRef : undefined} className={cn(isPinned && "min-h-full")}>
            {turn.map(message =>
              message.isUser ? (
                <UserMessage
                  key={message.id}
                  message={message}
                  evidences={evidences}
                  onArtefactClick={onArtefactClick}
                />
              ) : (
                <AssistantMessage
                  key={message.id}
                  message={message}
                  evidences={evidences}
                  isStreaming={isStreaming && message.id === streamingMessageId}
                  onArtefactClick={onArtefactClick}
                  inlineArtefacts={inlineArtefacts}
                />
              ),
            )}
          </div>
        );
      })}
    </ScrollableChat>
  );
};
